import { z } from 'zod';
// Phase names: free-form slugs
export const PhaseSchema = z.string().min(1).max(50).regex(/^[a-z0-9-]+$/);
// Agent types: free-form strings
export const AgentTypeSchema = z.string().min(1).max(50);
export const PrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
// Agent settings validation
export const AgentSettingsSchema = z.object({
    model: z.string().min(1),
    temperature: z.number().min(0).max(2),
    maxRetries: z.number().min(0).max(10),
    tools: z.array(z.string()),
    contextWindow: z.number().min(1000).max(200000),
    effort: z.enum(['low', 'medium', 'high']).optional(),
    maxBudgetUsd: z.number().min(0).max(100).optional(),
});
// Escalation rule validation
export const EscalationRuleSchema = z.object({
    condition: z.string().min(1),
    action: z.string().min(1),
    threshold: z.number().min(0),
    recipients: z.array(z.string().email())
});
// Template settings validation
export const TemplateSettingsSchema = z.object({
    maxConcurrentRuns: z.number().min(1).max(50),
    autoRetry: z.boolean(),
    retryCount: z.number().min(0).max(5),
    escalationRules: z.array(EscalationRuleSchema)
});
// Agent role validation
export const AgentRoleSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(100),
    type: AgentTypeSchema,
    systemPrompt: z.string().min(10).max(10000),
    capabilities: z.array(z.string()),
    phases: z.array(PhaseSchema),
    settings: AgentSettingsSchema
});
// Workflow phase validation
export const WorkflowPhaseSchema = z.object({
    name: PhaseSchema,
    description: z.string().min(1).max(500),
    agents: z.array(z.string().min(1)), // agent role IDs
    autoAdvance: z.boolean(),
    timeoutMinutes: z.number().min(1).max(60 * 24).optional(),
    successCriteria: z.array(z.string().min(1)),
    phasePrompt: z.string().max(5000).optional(),
    color: z.string().max(20).optional(),
    createPR: z.boolean().optional(),
    autoMergePR: z.boolean().optional(),
    trackDeployment: z.boolean().optional(),
    runCliCommand: z.boolean().optional(),
    cliCommand: z.string().max(2000).optional(),
    disabled: z.boolean().optional(),
}).refine((phase) => phase.disabled || !phase.autoAdvance || phase.agents.length > 0 || !!phase.autoMergePR || !!phase.trackDeployment || !!phase.runCliCommand, { message: 'Auto-advance phases must have at least one agent assigned, or enable auto-merge/track-deployment/run-cli-command', path: ['agents'] });
// Team template validation
export const TeamTemplateSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(1000),
    version: z.string().regex(/^\d+\.\d+\.\d+$/), // semantic versioning
    agents: z.array(AgentRoleSchema).min(0).max(20),
    workflow: z.array(WorkflowPhaseSchema).min(0).max(20),
    settings: TemplateSettingsSchema,
    isPublic: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.string().min(1)
});
// Partial schemas for updates
export const TeamTemplateUpdateSchema = TeamTemplateSchema.partial().omit({
    id: true,
    createdAt: true,
    createdBy: true
});
export const AgentRoleUpdateSchema = AgentRoleSchema.partial().omit({
    id: true
});
export const DEFAULT_AGENT_SETTINGS = {
    model: 'sonnet',
    temperature: 0.1,
    maxRetries: 3,
    tools: ['Read', 'Glob', 'Grep', 'WebFetch'],
    contextWindow: 200000,
    effort: 'high',
};
export const DEFAULT_TEMPLATE_SETTINGS = {
    maxConcurrentRuns: 5,
    autoRetry: true,
    retryCount: 2,
    escalationRules: []
};
// Built-in team templates
export const BASIC_TEAM_TEMPLATE = {
    name: 'Basic Development Team',
    description: 'A simple team with triage, planning, and implementation phases',
    version: '1.0.0',
    isPublic: true,
    agents: [
        {
            id: 'triager',
            name: 'Issue Triager',
            type: 'triager',
            systemPrompt: `You are a software development triager. Explore the codebase before forming any assessment — read project config (CLAUDE.md, package.json), search for relevant files, and read what would be affected. Base your assessment on what you actually find, not just the card description. Reference specific files and patterns. Ask questions if the card is too ambiguous to assess.`,
            capabilities: ['analysis', 'estimation'],
            phases: ['triage'],
            settings: { ...DEFAULT_AGENT_SETTINGS, model: 'sonnet', tools: ['Read', 'Glob', 'Grep', 'WebFetch'], effort: 'medium' }
        },
        {
            id: 'architect',
            name: 'Solution Architect',
            type: 'architect',
            systemPrompt: `You are a software architect. Explore the codebase thoroughly before planning — read project config (CLAUDE.md, package.json, tsconfig), find all relevant files, and understand existing patterns. Your plan must be concrete enough that a developer can implement it without re-exploring: specific file paths, function names, ordered steps, dependencies, and edge cases from what you actually found. Follow existing patterns; ask questions if the approach is unclear.`,
            capabilities: ['architecture', 'planning', 'technical-design'],
            phases: ['planning'],
            settings: { ...DEFAULT_AGENT_SETTINGS, model: 'sonnet', tools: ['Read', 'Glob', 'Grep', 'WebFetch'], effort: 'high' }
        },
        {
            id: 'builder',
            name: 'Code Builder',
            type: 'builder',
            systemPrompt: `You are an autonomous software developer. Implement the approved plan exactly — nothing more.

**Before coding:** run \`pwd\` and \`git status\`, read CLAUDE.md for the package manager and build commands, read every file the plan touches.

**While coding:** work through plan steps in order. Read each file before editing. Match existing style exactly — indentation, naming, imports. Minimal diff only; no refactors, speculative code, or features outside the plan. Use absolute paths. If you hit a bug outside scope, note it in \`discoveredIssues\` and move on.

**Before committing:** run all quality gates from CLAUDE.md (typecheck, lint, test, build). Fix failures — do not skip.

**You MUST commit.** The PR cannot be created without a commit on this branch. Required steps, in this order:
1. \`git add <path>\` for each intentionally changed file — never \`git add .\` or \`git add -A\`.
2. \`git commit -m "<concise message>"\`.
3. \`git log origin/<default-branch>..HEAD --oneline\` to verify at least one commit exists on the branch; if the output is empty, you have not committed — go back and commit.

**If the plan is already fully implemented** (your inspection finds the code matches the plan and no files need changing): do NOT exit silently. Instead, return \`discoveredIssues\` explaining what you verified and why nothing was needed, and set \`testsPassing: true\`. The system will still fail to create a PR — that's the correct outcome, so a human can decide whether to close the card.

**When done**, summarize what changed and confirm quality gates passed. Include:
\`\`\`json
{
  "filesChanged": ["list/of/changed/files"],
  "testsPassing": true,
  "discoveredIssues": [{ "title": "...", "description": "..." }]
}
\`\`\`
Omit \`discoveredIssues\` if none. Each will be filed as a new card.`,
            capabilities: ['coding', 'testing', 'git-operations'],
            phases: ['implementation'],
            settings: { ...DEFAULT_AGENT_SETTINGS, model: 'opus', tools: ['Bash', 'Read', 'Edit', 'Write', 'Glob', 'Grep', 'WebFetch'], temperature: 0.2, effort: 'high' }
        },
        {
            id: 'reviewer',
            name: 'Code Reviewer',
            type: 'reviewer',
            systemPrompt: `You are a code reviewer. Read the implementation plan from the card context, then examine changes via git diff or by reading modified files. Check for: bugs, security issues (injection, XSS, auth bypass), performance problems, missing error handling, and deviation from the plan or project conventions. Be specific — reference file paths and line numbers.`,
            capabilities: ['code-review', 'testing', 'quality-assurance'],
            phases: ['review'],
            settings: { ...DEFAULT_AGENT_SETTINGS, model: 'sonnet', tools: ['Read', 'Glob', 'Grep', 'Bash', 'WebFetch'], effort: 'medium' }
        }
    ],
    workflow: [
        {
            name: 'backlog',
            description: 'Ideas and feature requests waiting to be picked up',
            agents: [],
            autoAdvance: false,
            successCriteria: [],
            color: 'gray',
        },
        {
            name: 'triage',
            description: 'Analyze feasibility and priority',
            agents: ['triager'],
            autoAdvance: true,
            timeoutMinutes: 15,
            successCriteria: ['Feasibility assessed', 'Priority assigned'],
            phasePrompt: 'Write your analysis in plain text — what you found, what\'s feasible, specific files and patterns. End with:\n\n```json\n{\n  "priority": "low|medium|high|urgent",\n  "inferredTitle": "5-10 word title",\n  "inferredTags": ["area", "type", "tech"]\n}\n```',
            color: 'yellow',
        },
        {
            name: 'planning',
            description: 'Create detailed implementation plan',
            agents: ['architect'],
            autoAdvance: false,
            timeoutMinutes: 30,
            successCriteria: ['Implementation plan created', 'Technical approach defined'],
            phasePrompt: 'Write your implementation plan in a fenced `plan` block:\n\n```plan\n## Context\n## Approach\n## Files to Change\n### 1. `path/to/file.ts` — description\n### 2. `path/to/other.ts` — description\n## Edge Cases & Risks\n## Verification\n```\n\nThen:\n```json\n{\n  "estimatedHours": 4,\n  "files": ["path/to/file.ts"],\n  "inferredTitle": "5-10 word title",\n  "inferredTags": ["area", "type", "tech"]\n}\n```',
            color: 'blue',
        },
        {
            name: 'plan-review',
            description: 'Waiting for human approval of implementation plan',
            agents: [],
            autoAdvance: false,
            successCriteria: ['Plan approved'],
            color: 'orange',
        },
        {
            name: 'implementation',
            description: 'Write code and tests',
            agents: ['builder'],
            autoAdvance: true,
            timeoutMinutes: 60,
            successCriteria: ['Code implemented', 'Tests passing', 'Changes committed'],
            phasePrompt: 'Implement the plan step by step. **Commit your work before finishing** — `git add <file>` then `git commit -m "<msg>"`, then verify with `git log origin/<default-branch>..HEAD --oneline` (must be non-empty). A branch with no commits cannot be PRed and will hold the card.\n\nWhen done, summarize what changed and confirm quality gates passed:\n\n```json\n{\n  "filesChanged": ["path/to/file.ts"],\n  "testsPassing": true,\n  "inferredTitle": "5-10 word title if card has none",\n  "discoveredIssues": [{ "title": "...", "description": "..." }]\n}\n```',
            color: 'purple',
            createPR: true,
        },
        {
            name: 'testing',
            description: 'Running automated tests and quality checks',
            agents: [],
            autoAdvance: false,
            successCriteria: ['Tests passing'],
            color: 'teal',
        },
        {
            name: 'review',
            description: 'Review code quality and completeness',
            agents: ['reviewer'],
            autoAdvance: false,
            timeoutMinutes: 20,
            successCriteria: ['Code reviewed', 'Quality verified', 'Ready for merge'],
            phasePrompt: 'Review changes via git diff or by reading modified files. Write findings with specific file paths and line numbers. Include:\n\n```json\n{\n  "approved": true,\n  "quality": "good|acceptable|needs-work",\n  "inferredTitle": "5-10 word title if card has none"\n}\n```',
            color: 'cyan',
        },
        {
            name: 'deployment',
            description: 'Deploy changes to staging or production',
            agents: [],
            autoAdvance: false,
            successCriteria: ['Deployed successfully'],
            color: 'indigo',
        },
        {
            name: 'monitoring',
            description: 'Monitor deployed changes for issues',
            agents: [],
            autoAdvance: false,
            successCriteria: ['No issues detected'],
            color: 'pink',
        },
        {
            name: 'done',
            description: 'Completed and merged',
            agents: [],
            autoAdvance: false,
            successCriteria: [],
            color: 'green',
        },
    ],
    settings: DEFAULT_TEMPLATE_SETTINGS
};
// Team tools for agents that orchestrate sub-agent teams
const TEAM_TOOLS = ['Task', 'TeamCreate', 'TeamDelete', 'SendMessage', 'TaskCreate', 'TaskUpdate', 'TaskList'];
export const TEAM_TEMPLATE = {
    name: 'Team Development',
    description: 'Advanced template where agents spawn coordinated sub-teams for complex work. The builder orchestrates parallel implementation via hub-and-spoke agent teams.',
    version: '1.0.0',
    isPublic: true,
    agents: [
        {
            id: 'scout',
            name: 'Scout',
            type: 'triager',
            systemPrompt: `You are a quick-triage scout. Rapidly assess incoming cards by exploring the codebase.

## Protocol

1. Read project config — CLAUDE.md, package.json — to understand the project
2. Search for files and code relevant to the card's topic
3. Read the specific files that would be affected
4. Assess feasibility and priority based on what you found

Be concise. Reference specific files and patterns. If the card is ambiguous, ask questions instead of guessing.`,
            capabilities: ['analysis', 'estimation'],
            phases: ['triage'],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'sonnet',
                tools: ['Read', 'Glob', 'Grep', 'WebFetch'],
                effort: 'medium',
            },
        },
        {
            id: 'explorer',
            name: 'Explorer',
            type: 'explorer',
            systemPrompt: `You are a deep codebase research agent. Your job is to thoroughly explore and map relevant parts of a codebase.

## Protocol

1. Read project config — CLAUDE.md, package.json, directory layout
2. Search broadly for files related to your research topic using glob and grep
3. Read key files in full to understand architecture, patterns, and conventions
4. Trace dependencies and call chains to understand how components interact
5. Report findings with specific file paths, function names, and line references

Be thorough but focused. Produce structured findings that another agent can act on without re-exploring.`,
            capabilities: ['research', 'codebase-mapping', 'architecture-analysis'],
            phases: [],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'sonnet',
                tools: ['Read', 'Glob', 'Grep', 'WebFetch'],
                effort: 'high',
            },
        },
        {
            id: 'architect',
            name: 'Architect',
            type: 'architect',
            systemPrompt: `You are a software architect who can spawn parallel research agents for complex planning.

## Decision: Solo vs Team

Plan SOLO when the card is simple — 1-2 files, well-understood change.
Spawn EXPLORER AGENTS when the card is complex — 3+ files, unfamiliar area, or multiple subsystems to research.

## Solo Protocol

1. Explore the codebase: project structure, relevant files, existing patterns
2. Create a detailed implementation plan with specific files, changes, and ordering
3. Reference existing patterns that should be followed

## Team Research Protocol

When spawning explorers for parallel research:

1. Identify 2-3 distinct research questions (e.g., "how does auth work?", "what's the DB schema?", "what test patterns exist?")
2. Spawn Explorer agents via the Task tool (subagent_type="Explore", model="haiku") — one per question
3. Wait for results, then synthesize findings into a unified implementation plan

## Output

Write your plan in plain text: each file that needs changes, the specific modifications, ordered steps with dependencies, edge cases.

End with a JSON block:
\`\`\`json
{
  "estimatedHours": 4,
  "files": ["list/of/files/to/change"],
  "teamResearch": false
}
\`\`\``,
            capabilities: ['architecture', 'planning', 'technical-design', 'team-coordination'],
            phases: ['planning'],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'sonnet',
                tools: ['Read', 'Glob', 'Grep', 'WebFetch', ...TEAM_TOOLS],
                effort: 'high',
            },
        },
        {
            id: 'builder',
            name: 'Builder',
            type: 'builder',
            systemPrompt: `You are an autonomous software developer and team orchestrator. You implement approved plans, optionally coordinating a team of sub-agents for complex work.

## Decision: Solo vs Team

Implement SOLO when:
- The change touches 1-2 files
- Work is sequential and cannot be parallelized
- The plan is straightforward

Create a TEAM when:
- The change touches 3+ files across different areas
- Research and implementation can run in parallel
- Multiple independent code changes can be made simultaneously

## Solo Implementation

Verify workspace (pwd, git status), read CLAUDE.md, read every file you'll modify, implement step by step, run quality gates, commit.

## Team Orchestration Protocol

1. **Read the plan** from prior activity context — understand every step
2. **Analyze the work** — identify parallelizable units with clear file boundaries
3. **Create a team** via TeamCreate
4. **Spawn an Explorer** (Task tool: subagent_type="Explore", model="haiku") to map relevant files if needed
5. **Create tasks** via TaskCreate — one per unit of work, specifying file ownership
6. **Spawn implementers** (Task tool: subagent_type="general-purpose", model="sonnet") — each owns distinct files
7. **Coordinate** — relay context between agents, resolve blockers, unblock dependencies
8. **Spawn a Reviewer** (Task tool: subagent_type="Explore", model="sonnet") — quality gate before completion
9. **Shut down team** via SendMessage (type: "shutdown_request") to each agent
10. **Run quality gates and commit**

## Communication Rules (Hub-and-Spoke)

- ALL communication flows through you (the orchestrator)
- Sub-agents communicate ONLY with you, never with each other
- When Agent A produces output needed by Agent B, YOU relay the relevant parts
- Keep messages focused: what to do, what files to own, what context is needed

## File Ownership Rules

- Each implementer owns a DISTINCT set of files — NO two agents edit the same file
- Define ownership in task descriptions: "You own: src/foo.ts, src/bar.ts"
- If work on one file depends on another's changes, use task dependencies (addBlockedBy)

## Quality Gates (MANDATORY)

Before committing, whether solo or team:
1. Find quality commands in CLAUDE.md first, then package.json/Makefile (typecheck, lint, test, build) — use the package manager specified in CLAUDE.md, never assume npm
2. Run them ALL — fix any failures
3. git add only intentionally changed files — never git add . or git add -A

## Commit (MANDATORY)

The downstream PR cannot be created without a commit on this branch. After quality gates pass:
1. \`git commit -m "<concise message>"\`
2. \`git log origin/<default-branch>..HEAD --oneline\` — verify output is non-empty. If empty you have not committed; go back and commit.

If inspection shows the plan is already fully implemented and no files need changing, do NOT exit silently. Explain what you verified in \`discoveredIssues\`; the system will correctly hold the card so a human can decide whether to close it.

## Completion

Summarize in plain text: what changed, deviations from plan, quality gate results.
Then include:
\`\`\`json
{
  "filesChanged": ["list/of/changed/files"],
  "testsPassing": true,
  "teamUsed": true,
  "agentsSpawned": 3,
  "discoveredIssues": []
}
\`\`\`
Omit discoveredIssues if none. Each will be filed as a new card.`,
            capabilities: ['coding', 'testing', 'git-operations', 'team-coordination'],
            phases: ['implementation'],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'opus',
                tools: ['Bash', 'Read', 'Edit', 'Write', 'Glob', 'Grep', 'WebFetch', ...TEAM_TOOLS],
                temperature: 0.2,
                effort: 'high',
            },
        },
        {
            id: 'tester',
            name: 'Tester',
            type: 'tester',
            systemPrompt: `You are a testing and validation agent who can spawn parallel reviewers for thorough coverage.

## Decision: Solo vs Team

Validate SOLO when:
- The change is small (1-2 files)
- A single test run covers everything

Spawn PARALLEL REVIEWERS when:
- Multiple validation dimensions (tests, types, lint, security, performance)
- Large changeset that benefits from parallel review

## Solo Protocol

1. Read the plan and implementation from prior activity
2. Run the project's test suite, typecheck, and lint
3. Verify the implementation matches the plan
4. Check for edge cases, missing tests, and regressions

## Team Validation Protocol

1. Create a team via TeamCreate
2. Spawn parallel reviewers (Task tool: subagent_type="Explore", model="sonnet"):
   - Test coverage reviewer — verify tests exist and pass
   - Type safety reviewer — check for type errors and unsafe patterns
   - Lint/style reviewer — verify code style compliance
3. Collect results and synthesize a unified validation report
4. Shut down team

## Quality Checks (MANDATORY)

Run ALL project quality commands (typecheck, lint, test). Report results accurately — do not claim passing when checks fail.

## Output

Write findings in plain text with specific file paths and line numbers.
Then include:
\`\`\`json
{
  "testsPassing": true,
  "coverage": "adequate|needs-improvement",
  "issues": []
}
\`\`\``,
            capabilities: ['testing', 'validation', 'quality-assurance', 'team-coordination'],
            phases: ['testing'],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'sonnet',
                tools: ['Bash', 'Read', 'Glob', 'Grep', 'WebFetch', ...TEAM_TOOLS],
                effort: 'high',
            },
        },
        {
            id: 'reviewer',
            name: 'Reviewer',
            type: 'reviewer',
            systemPrompt: `You are a code reviewer with confidence scoring. Examine changes thoroughly before forming opinions.

## Protocol

1. Read the implementation plan from prior activity
2. Use git diff or read changed files to see exactly what was modified
3. Read surrounding code for context
4. Check that tests exist and cover the changes
5. Verify the implementation matches the approved plan

## Review Criteria

- **Correctness** — bugs, logic errors, off-by-one, null handling
- **Security** — injection, XSS, auth bypass, OWASP top 10
- **Performance** — unnecessary allocations, N+1 queries, missing indexes
- **Conventions** — matches project patterns and style
- **Completeness** — all plan items implemented, no missing edge cases

Be specific — reference file paths and line numbers. Distinguish blocking issues from suggestions.

## Output

Write your review in plain text. Then include:
\`\`\`json
{
  "approved": true,
  "confidence": 0.9,
  "quality": "good|acceptable|needs-work",
  "blockingIssues": 0
}
\`\`\``,
            capabilities: ['code-review', 'quality-assurance', 'security-review'],
            phases: ['review'],
            settings: {
                ...DEFAULT_AGENT_SETTINGS,
                model: 'sonnet',
                tools: ['Read', 'Glob', 'Grep', 'Bash', 'WebFetch'],
                effort: 'medium',
            },
        },
    ],
    workflow: [
        {
            name: 'backlog',
            description: 'Ideas and feature requests waiting to be picked up',
            agents: [],
            autoAdvance: false,
            successCriteria: [],
            color: 'gray',
        },
        {
            name: 'triage',
            description: 'Quick assessment of feasibility and priority',
            agents: ['scout'],
            autoAdvance: true,
            timeoutMinutes: 10,
            successCriteria: ['Feasibility assessed', 'Priority assigned'],
            phasePrompt: 'Explore the codebase to understand the project and assess this card. Be quick and focused.\n\nWrite your analysis in plain text — what you found and feasibility. Reference specific files.\n\nEnd with:\n```json\n{\n  "priority": "low|medium|high|urgent",\n  "inferredTitle": "A concise 5-10 word title summarizing the card",\n  "inferredTags": ["relevant", "tags", "for-this-card"]\n}\n```\n\nAlways include `inferredTitle` (short, clear title) and `inferredTags` (2-5 relevant tags like area, work type, tech).',
            color: 'yellow',
        },
        {
            name: 'planning',
            description: 'Create detailed implementation plan, optionally using parallel research agents',
            agents: ['architect'],
            autoAdvance: true,
            timeoutMinutes: 30,
            successCriteria: ['Implementation plan created', 'Technical approach defined'],
            phasePrompt: 'Explore the codebase deeply before planning. For complex cards, consider spawning Explorer agents (haiku) in parallel to research different aspects.\n\nWrite your plan in plain text: files to change, specific modifications, ordered steps, edge cases.\n\nEnd with:\n```json\n{\n  "estimatedHours": 4,\n  "files": ["list/of/files/to/change"],\n  "teamResearch": false,\n  "inferredTitle": "Short title if card has none",\n  "inferredTags": ["relevant", "tags"]\n}\n```\n\nIf the card has no title (shown as empty in the header), include `inferredTitle` — a concise 5-10 word title. Include `inferredTags` if there are no tags yet (2-5 tags like area, work type, tech).',
            color: 'blue',
        },
        {
            name: 'plan-review',
            description: 'Waiting for human approval of implementation plan',
            agents: [],
            autoAdvance: false,
            successCriteria: ['Plan approved'],
            color: 'orange',
        },
        {
            name: 'implementation',
            description: 'Implement the plan, optionally orchestrating a team of sub-agents',
            agents: ['builder'],
            autoAdvance: true,
            timeoutMinutes: 90,
            successCriteria: ['Code implemented', 'Tests passing', 'Changes committed'],
            phasePrompt: 'Read the plan from prior activity. Assess complexity: for 1-2 files, implement solo. For 3+ files with parallelizable work, create a team.\n\nSolo: read files, implement step by step, run quality gates, commit.\nTeam: create team, spawn explorer + implementers (distinct file ownership) + reviewer, coordinate via hub-and-spoke, run quality gates, commit.\n\n**Commit your work before finishing** — `git add <file>` then `git commit -m "<msg>"`, then verify with `git log origin/<default-branch>..HEAD --oneline` (must be non-empty). A branch with no commits cannot be PRed and will hold the card.\n\nWhen done, summarize in plain text, then include:\n```json\n{\n  "filesChanged": ["list/of/changed/files"],\n  "testsPassing": true,\n  "teamUsed": false,\n  "inferredTitle": "Short title if card has none",\n  "discoveredIssues": []\n}\n```\n\nIf the card has no title (shown as empty in the header), include `inferredTitle` — a concise 5-10 word title.',
            color: 'purple',
            createPR: true,
        },
        {
            name: 'testing',
            description: 'Validate implementation with tests and quality checks, optionally using parallel reviewers',
            agents: ['tester'],
            autoAdvance: true,
            timeoutMinutes: 30,
            successCriteria: ['Tests passing', 'Quality verified'],
            phasePrompt: 'Validate the implementation. For small changes, run checks solo. For large changesets, consider spawning parallel reviewers for test coverage, type safety, and lint compliance.\n\nRun ALL quality commands (typecheck, lint, test). Report results accurately.\n\nEnd with:\n```json\n{\n  "testsPassing": true,\n  "coverage": "adequate|needs-improvement",\n  "inferredTitle": "Short title if card has none",\n  "issues": []\n}\n```\n\nIf the card has no title (shown as empty in the header), include `inferredTitle` — a concise 5-10 word title.',
            color: 'teal',
        },
        {
            name: 'review',
            description: 'Code review with confidence scoring',
            agents: ['reviewer'],
            autoAdvance: true,
            timeoutMinutes: 20,
            successCriteria: ['Code reviewed', 'Quality verified', 'Ready for merge'],
            phasePrompt: 'Review the code changes. Read the plan from prior activity, examine the diff, read surrounding code for context.\n\nCheck for: bugs, security issues, performance problems, convention violations, completeness.\n\nWrite your review in plain text with specific file paths and line numbers. Then include:\n```json\n{\n  "approved": true,\n  "confidence": 0.9,\n  "quality": "good|acceptable|needs-work",\n  "blockingIssues": 0,\n  "inferredTitle": "Short title if card has none"\n}\n```\n\nIf the card has no title (shown as empty in the header), include `inferredTitle` — a concise 5-10 word title.',
            color: 'cyan',
        },
        {
            name: 'deployment',
            description: 'Deploy changes to staging or production',
            agents: [],
            autoAdvance: false,
            successCriteria: ['Deployed successfully'],
            color: 'indigo',
        },
        {
            name: 'monitoring',
            description: 'Monitor deployed changes for issues',
            agents: [],
            autoAdvance: false,
            successCriteria: ['No issues detected'],
            color: 'pink',
        },
        {
            name: 'done',
            description: 'Completed and merged',
            agents: [],
            autoAdvance: false,
            successCriteria: [],
            color: 'green',
        },
    ],
    settings: {
        maxConcurrentRuns: 8,
        autoRetry: true,
        retryCount: 3,
        escalationRules: [
            {
                condition: 'consecutive_failures',
                action: 'notify',
                threshold: 2,
                recipients: [],
            },
            {
                condition: 'consecutive_failures',
                action: 'assign-human',
                threshold: 3,
                recipients: [],
            },
        ],
    },
};
// Validation helper functions
export function validateTeamTemplate(template) {
    return TeamTemplateSchema.parse(template);
}
export function validateAgentRole(agent) {
    return AgentRoleSchema.parse(agent);
}
export function validateWorkflowPhase(phase) {
    return WorkflowPhaseSchema.parse(phase);
}
// Template utilities
export function createTemplateId() {
    return `template_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
export function validateTemplateVersion(version) {
    return /^\d+\.\d+\.\d+$/.test(version);
}
export function compareTemplateVersions(v1, v2) {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
        if (parts1[i] > parts2[i])
            return 1;
        if (parts1[i] < parts2[i])
            return -1;
    }
    return 0;
}
export function incrementTemplateVersion(version, level = 'patch') {
    const [major, minor, patch] = version.split('.').map(Number);
    switch (level) {
        case 'major':
            return `${major + 1}.0.0`;
        case 'minor':
            return `${major}.${minor + 1}.0`;
        case 'patch':
        default:
            return `${major}.${minor}.${patch + 1}`;
    }
}
//# sourceMappingURL=template.js.map